import axios from 'axios';
import utilsStorage from '@/utils/storage';
import i18n from '@/plugins/i18n';
import { localizeErrorResponse } from '@/utils/backend-message-i18n';
import { withLocaleHeader } from '@/utils/locale-propagation';

let configCache = null;
let configPromise = null;

async function loadConfig() {
  if (configCache) return configCache;

  if (!configPromise) {
    configPromise = fetch('/config.json')
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');

        return response.json();
      })
      .then((data) => {
        configCache = data;

        return data;
      })
      .catch(() => {
        configPromise = null;

        return null;
      });
  }

  return configPromise;
}

/* Textos montados aqui (não vêm do backend), no idioma da interface. */
const offlineMessage = () => i18n.t('http.connectionUi.offline');

const serverUnreachableMessage = () => i18n.t('http.connectionUi.serverUnreachable');

export const sessionExpiredMessage = () => i18n.t('http.connectionUi.sessionExpired');

/* Sem resposta do servidor: quando o navegador sabe que está offline, o
   problema é a internet do usuário, não o sistema. */
export function describeConnectionError() {
  const message = navigator.onLine === false ? offlineMessage() : serverUnreachableMessage();

  return `${message} (Ref.: ${CONNECTION_REFERENCE})`;
}

const CONNECTION_REFERENCE = 'SEM-CONEXAO';
const REFERENCE_PATTERN = /\(Ref\.:/;
const CORRELATION_REFERENCE_LENGTH = 8;
const MESSAGE_FIELDS = ['mensagem', 'message', 'error'];

function _responseReference(data, status) {
  return data?.errorCode || String(data?.correlationId || '').slice(0, CORRELATION_REFERENCE_LENGTH) || `HTTP-${status}`;
}

/* Toda mensagem de erro do servidor chega à tela com o código para o suporte.
   O errorParser do backend já acrescenta "(Ref.: …)"; respostas fora do padrão
   (campo message/error, sem código) recebem aqui o código ou o status HTTP. */
export function ensureReferenceOnErrorResponse(error) {
  const data = error?.response?.data;

  if (!data || typeof data !== 'object') return error;

  // Mensagem do backend (sempre em português) no idioma da interface, antes de qualquer tela usar
  localizeErrorResponse(data);

  const field = MESSAGE_FIELDS.find((name) => typeof data[name] === 'string' && data[name].trim());

  if (!field || REFERENCE_PATTERN.test(data[field])) return error;

  const withReference = `${data[field].trim().replace(/\.$/, '')}. (Ref.: ${_responseReference(data, error.response.status)})`;

  data[field] = withReference;

  if (!data.mensagem) data.mensagem = withReference;

  return error;
}

async function getApi() {
  const config = await loadConfig();

  const api = axios.create({
    baseURL: config?.urlApi
  });

  api.interceptors.request.use(async (config) => {
    const token = await utilsStorage.getTokenOnStorage();
    config.headers.Authorization = token;

    return withLocaleHeader(config);
  });

  const isPublicRoute = () => {
    const currentPath = window.location.pathname;
    // Precisa espelhar as rotas com meta.requireAuth === false no router:
    // sem isso um 401 de fundo derruba a página pública para o /login.
    const publicPaths = ['/login', '/assinatura', '/confirmacao-assinatura', '/ui'];

    return publicPaths.some(path => currentPath.includes(path));
  };

  api.interceptors.response.use(
    (response) => {
      if (response.status === 401 && response.config.url !== '/login' && !isPublicRoute()) {
        utilsStorage.removeAuth();

        setTimeout(() => {
          // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
          // este disparo o usuário pode ter ido para uma rota pública, e o
          // redirect pendente o arrancava dela.
          if (isPublicRoute()) return;

          // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
          utilsStorage.saveCurrentLocationAsReturnPath();
          window.location.href = '/login';
        }, 1000);
      }

      return response;
    },
    (error) => {
      if (!error.response) return Promise.reject(describeConnectionError());

      const isUnauthorized = error.response.status === 401;
      const isLoginRoute = error.response.config.url === '/login';
      const hasValidBaseUrl = !!error.response.config.baseURL;

      if (isUnauthorized && !isLoginRoute && !isPublicRoute() && hasValidBaseUrl) {
        utilsStorage.removeAuth();

        setTimeout(() => {
          // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
          // este disparo o usuário pode ter ido para uma rota pública, e o
          // redirect pendente o arrancava dela.
          if (isPublicRoute()) return;

          // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
          utilsStorage.saveCurrentLocationAsReturnPath();
          window.location.href = '/login';
        }, 3000);

        return Promise.reject(sessionExpiredMessage());
      }

      return Promise.reject(ensureReferenceOnErrorResponse(error));
    }
  );

  return api;
}

export default getApi;
